<template>
  <div class="rfm-audiencia-page">
    <!-- Header -->
    <div class="page-header mb-4">
      <div class="header-content">
        <div class="header-actions">
          <b-button
            variant="outline-secondary"
            size="sm"
            :disabled="carregando"
            class="w-100 w-md-auto"
            @click="carregarDadosRFM"
          >
            <i class="feather icon-refresh-cw mr-1" />
            Atualizar
          </b-button>
        </div>
      </div>
    </div>

    <!-- Seletor de Loja -->
    <b-row
      v-if="stores.length > 1"
      class="mb-4 justify-content-center"
    >
      <b-col
        cols="12"
        md="6"
        lg="4"
      >
        <div class="store-selector text-center">
          <label class="form-label mb-1 text-muted small">Selecionar Loja:</label>
          <b-form-select
            v-model="storeUuid"
            :options="storeOptions"
            size="sm"
            @change="onStoreChange"
          >
            <template #first>
              <b-form-select-option
                :value="''"
                disabled
              >
                Escolha uma loja...
              </b-form-select-option>
            </template>
          </b-form-select>
        </div>
      </b-col>
    </b-row>

    <!-- Período de análise -->
    <b-card class="mb-4 period-card">
      <b-card-body>
        <div class="period-content">
          <div class="period-info mb-3 mb-md-0">
            <h6 class="mb-1">
              <i class="feather icon-calendar mr-2" />
              Período de Análise RFM
            </h6>
            <p class="text-muted mb-0 small">
              Os dados são calculados com base nos últimos 6 meses por padrão
            </p>
          </div>
          <div class="period-inputs">
            <div class="d-flex flex-column flex-md-row align-items-stretch align-items-md-center gap-2">
              <b-form-input
                v-model="dataInicio"
                type="date"
                size="sm"
                class="date-input"
              />
              <span class="text-muted text-center d-none d-md-inline">até</span>
              <b-form-input
                v-model="dataFim"
                type="date"
                size="sm"
                class="date-input"
              />
              <b-button
                variant="primary"
                size="sm"
                :disabled="carregando"
                class="apply-btn"
                @click="carregarDadosRFM"
              >
                <b-spinner
                  v-if="carregando"
                  small
                  class="mr-1"
                />
                Aplicar
              </b-button>
            </div>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <!-- Loading -->
    <div
      v-if="carregando"
      class="text-center py-5"
    >
      <b-spinner
        variant="primary"
        style="width: 3rem; height: 3rem;"
      />
      <p class="text-muted mt-3">
        Carregando análise RFM...
      </p>
    </div>

    <!-- Conteúdo Principal -->
    <div v-else>
      <!-- Cards de Segmentos RFM -->
      <b-row class="mb-4">
        <b-col cols="12">
          <h5 class="mb-3">
            <i class="feather icon-pie-chart mr-2" />
            Selecione os Segmentos para sua Audiência
          </h5>
          <p class="text-muted mb-3">
            Clique nos segmentos que deseja incluir na sua audiência. Você pode selecionar múltiplos segmentos.
          </p>
        </b-col>
      </b-row>

      <!-- Grid de Segmentos -->
      <b-row>
        <b-col
          v-for="segmento in segmentosDisponiveis"
          :key="segmento.id"
          cols="1/2"
          sm="6"
          lg="4"
          class="mb-4"
        >
          <b-card
            :class="[
              'segment-card h-100 cursor-pointer',
              { 'selected': segmentosSelecionados.includes(segmento.id) },
              `segment-${segmento.cssClass}`
            ]"
            @click="toggleSegmento(segmento.id)"
          >
            <b-card-body>
              <div class="d-flex justify-content-between align-items-start mb-3">
                <div class="flex-grow-1">
                  <div class="d-flex align-items-center mb-2">
                    <div
                      class="segment-icon mr-2"
                      :style="{ backgroundColor: segmento.cor + '20', color: segmento.cor }"
                    >
                      <i :class="getSegmentIcon(segmento.id)" />
                    </div>
                    <h6 class="mb-0 font-weight-bold">
                      {{ segmento.nomeDisplay }}
                    </h6>
                  </div>
                  <small class="text-muted d-block segment-description">{{ segmento.descricao }}</small>
                </div>
                <b-form-checkbox
                  :checked="segmentosSelecionados.includes(segmento.id)"
                  class="ml-2"
                  @change="toggleSegmento(segmento.id)"
                  @click.stop
                />
              </div>

              <div class="segment-stats">
                <div class="d-flex justify-content-between mb-2">
                  <span class="text-muted small">Total de Leads:</span>
                  <strong :style="{ color: segmento.cor }">
                    {{ formatNumber(getSegmentoTotal(segmento.id)) }}
                  </strong>
                </div>
                <div class="d-flex justify-content-between">
                  <span class="text-muted small">% do Total:</span>
                  <strong class="small">{{ calcularPercentual(segmento.id) }}%</strong>
                </div>
              </div>

              <div class="segment-action">
                <b-badge
                  :variant="segmento.corBadge"
                  class="w-100 text-center py-1"
                  style="font-size: 0.75rem;"
                >
                  {{ segmento.acao }}
                </b-badge>
              </div>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <!-- Resumo da Seleção -->
      <b-card
        v-if="segmentosSelecionados.length > 0"
        class="mt-4 selection-summary"
      >
        <b-card-body>
          <!-- Cabeçalho do resumo - responsivo -->
          <div class="summary-header mb-3">
            <div class="summary-title mb-3 mb-md-0">
              <h5 class="mb-2">
                <i class="feather icon-check-circle text-success mr-2" />
                Resumo da Seleção
              </h5>
              <div class="d-flex flex-wrap">
                <b-badge
                  v-for="segId in segmentosSelecionados"
                  :key="segId"
                  :variant="getSegmentoBadgeVariant(segId)"
                  class="mr-2 mb-2 badge-removable"
                >
                  {{ getSegmentoNome(segId) }}
                  <i
                    class="feather icon-x ml-1 cursor-pointer"
                    @click.stop="toggleSegmento(segId)"
                  />
                </b-badge>
              </div>
            </div>
            <div class="summary-stats text-center text-md-right">
              <h3 class="mb-0 text-primary">
                {{ formatNumber(totalLeadsSelecionados) }}
              </h3>
              <small class="text-muted">leads selecionados</small>
            </div>
          </div>

          <hr class="my-3">

          <!-- Filtro RFM 3D - Qualificação Monetária -->
          <div class="rfm-3d-filter mb-3">
            <div class="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between">
              <div class="filter-info mb-2 mb-md-0">
                <div class="d-flex align-items-center">
                  <b-form-checkbox
                    v-model="qualificarPorMonetario"
                    switch
                    size="lg"
                    class="mr-2"
                    @change="onQualificacaoChange"
                  >
                    <span class="font-weight-bold">Qualificar por Alto Valor (RFM 3D)</span>
                  </b-form-checkbox>
                  <i
                    v-b-tooltip.hover
                    class="feather icon-info text-muted cursor-pointer"
                    title="Filtra apenas clientes com Score Monetário > 3 (alto valor). Útil para campanhas focadas em clientes de alto ticket."
                  />
                </div>
                <small class="text-muted d-block mt-1">
                  <i class="feather icon-filter mr-1" />
                  Mantém apenas clientes com alto valor monetário (M > 3) dos segmentos selecionados
                </small>
              </div>
              <div
                v-if="qualificarPorMonetario"
                class="filter-badge"
              >
                <b-badge
                  variant="light-success"
                  class="px-3 py-2"
                >
                  <i class="feather icon-dollar-sign mr-1" />
                  Filtro 3D Ativo
                </b-badge>
              </div>
            </div>

            <!-- Comparação de contagem -->
            <div
              v-if="qualificarPorMonetario && totalLeadsSelecionados !== totalLeadsSemFiltro"
              class="filter-comparison mt-2"
            >
              <div class="d-flex align-items-center">
                <span class="text-muted small mr-2">
                  <del>{{ formatNumber(totalLeadsSemFiltro) }} leads (2D)</del>
                </span>
                <i class="feather icon-arrow-right text-success mx-2" />
                <span class="text-success font-weight-bold">
                  {{ formatNumber(totalLeadsSelecionados) }} leads de alto valor (3D)
                </span>
                <b-badge
                  variant="light-warning"
                  class="ml-2"
                >
                  -{{ calcularReducaoPercentual() }}%
                </b-badge>
              </div>
            </div>
          </div>

          <hr class="my-3">

          <!-- Exclusão de Audiências Existentes -->
          <div
            v-if="audienciasParaExclusao.length > 0"
            class="mb-3"
          >
            <b-form-group class="mb-2">
              <template #label>
                <div class="d-flex align-items-center">
                  <i class="feather icon-user-minus mr-2 text-warning" />
                  <span>Excluir Leads de Outras Audiências</span>
                </div>
              </template>
              <v-select
                v-model="audienciasExcluir"
                :options="audienciasParaExclusao"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :reduce="aud => aud.id"
                label="name"
                multiple
                placeholder="Selecione audiências para excluir seus leads..."
                :loading="carregandoAudiencias"
                :disabled="carregandoAudiencias || salvandoAudiencia"
              >
                <template #option="{ name, lead_count, created_at }">
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>{{ name }}</strong>
                      <small
                        v-if="lead_count"
                        class="text-muted d-block"
                      >
                        {{ lead_count }} leads
                      </small>
                    </div>
                    <small class="text-muted">
                      {{ formatarDataAudiencia(created_at) }}
                    </small>
                  </div>
                </template>
                <template #selected-option="{ name }">
                  <span>{{ name }}</span>
                </template>
                <template #no-options>
                  <span class="text-muted">Nenhuma audiência encontrada</span>
                </template>
              </v-select>
              <small class="text-muted d-block mt-1">
                <i class="feather icon-info mr-1" />
                Leads dessas audiências NÃO aparecerão na nova audiência
              </small>
            </b-form-group>
            <small
              v-if="audienciasExcluir.length > 0"
              class="text-info d-block mb-2"
            >
              {{ audienciasExcluir.length }} audiência(s) selecionada(s) para exclusão
            </small>
          </div>

          <hr class="my-3">

          <!-- Ações - responsivo -->
          <div class="actions-section">
            <div
              class="actions-section-fields d-flex flex-column flex-md-row justify-content-md-between align-items-stretch align-items-md-start gap-3 w-100"
            >
              <b-form-group
                label="Nome da Audiência RFM"
                class="mb-0 actions-field-nome flex-grow-1"
              >
                <b-form-input
                  v-model="nomeAudiencia"
                  placeholder="Ex: Campanha Reengajamento - Em Risco"
                  size="sm"
                />
              </b-form-group>
              <b-form-group
                label="Quantidade de leads"
                class="mb-0 actions-field-limite flex-shrink-0"
              >
                <b-form-input
                  v-model.number="limiteLeadsRFM"
                  type="number"
                  min="1"
                  max="50000"
                  placeholder="1000"
                  size="sm"
                  :disabled="salvandoAudiencia"
                  class="actions-input-limite"
                />
                <small class="text-muted d-block mt-1">
                  Máx. 50.000. Ordenação: melhores scores R, F e M primeiro.
                </small>
              </b-form-group>
            </div>
            <div class="action-buttons">
              <b-button
                variant="outline-secondary"
                size="sm"
                class="w-100 w-md-auto mb-2 mb-md-0"
                @click="limparSelecao"
              >
                <i class="feather icon-x mr-1" />
                Limpar
              </b-button>
              <b-button
                variant="outline-primary"
                size="sm"
                :disabled="carregandoLeads"
                class="w-100 w-md-auto mb-2 mb-md-0"
                @click="visualizarLeads"
              >
                <b-spinner
                  v-if="carregandoLeads"
                  small
                  class="mr-1"
                />
                <i
                  v-else
                  class="feather icon-eye mr-1"
                />
                Visualizar Leads
              </b-button>
              <b-button
                variant="primary"
                size="sm"
                :disabled="salvandoAudiencia || !nomeAudiencia.trim()"
                class="w-100 w-md-auto"
                @click="criarAudienciaRFM"
              >
                <b-spinner
                  v-if="salvandoAudiencia"
                  small
                  class="mr-1"
                />
                <i
                  v-else
                  class="feather icon-save mr-1"
                />
                Criar Audiência
              </b-button>
            </div>
          </div>
        </b-card-body>
      </b-card>

      <!-- Estado Vazio -->
      <b-card
        v-else
        class="mt-4 text-center py-5"
      >
        <i
          class="feather icon-target text-muted"
          style="font-size: 4rem;"
        />
        <h5 class="text-muted mt-3">
          Nenhum segmento selecionado
        </h5>
        <p class="text-muted">
          Selecione um ou mais segmentos RFM acima para criar uma audiência direcionada.
        </p>
      </b-card>
    </div>

    <!-- Modal de Visualização de Leads -->
    <b-modal
      v-model="modalLeadsAberto"
      title="Leads dos Segmentos Selecionados"
      size="xl"
      scrollable
      hide-footer
    >
      <div
        v-if="carregandoLeadsModal"
        class="text-center py-5"
      >
        <b-spinner variant="primary" />
        <p class="text-muted mt-2">
          Carregando leads...
        </p>
      </div>

      <div v-else>
        <!-- Estatísticas -->
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div>
            <strong>{{ leadsVisualizacao.length }}</strong> leads encontrados
            <span
              v-if="totalLeadsAPI > leadsVisualizacao.length"
              class="text-muted"
            >
              (mostrando primeiros {{ leadsVisualizacao.length }} de {{ totalLeadsAPI }})
            </span>
          </div>
          <b-button
            variant="outline-success"
            size="sm"
            @click="exportarCSV"
          >
            <i class="feather icon-download mr-1" />
            Exportar CSV
          </b-button>
        </div>

        <!-- Tabela de Leads -->
        <div class="table-responsive">
          <table class="table table-hover table-striped">
            <thead class="thead-light">
              <tr>
                <th>Nome</th>
                <th>Telefone</th>
                <th>Email</th>
                <th class="text-center">
                  Segmento
                </th>
                <th class="text-center">
                  Score R
                </th>
                <th class="text-center">
                  Score F
                </th>
                <th class="text-center">
                  Score M
                </th>
                <th class="text-right">
                  Valor Total
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="lead in leadsVisualizacao"
                :key="lead.cliente_store_id || lead.id"
              >
                <td>{{ lead.nome || lead.name || '-' }}</td>
                <td>
                  <code class="text-primary">{{ lead.phone || lead.telefone || '-' }}</code>
                </td>
                <td>{{ lead.email || '-' }}</td>
                <td class="text-center">
                  <b-badge :variant="getSegmentoBadgeVariant(lead.segmento_id || lead.segmento)">
                    {{ getSegmentoNome(lead.segmento_id || lead.segmento) }}
                  </b-badge>
                </td>
                <td class="text-center">
                  <b-badge variant="light-primary">
                    R{{ getLeadScoreR(lead) }}
                  </b-badge>
                </td>
                <td class="text-center">
                  <b-badge variant="light-info">
                    F{{ getLeadScoreF(lead) }}
                  </b-badge>
                </td>
                <td class="text-center">
                  <b-badge :variant="getScoreMonetario(lead) > 3 ? 'light-success' : 'light-secondary'">
                    M{{ getScoreMonetario(lead) || '-' }}
                  </b-badge>
                </td>
                <td class="text-right">
                  R$ {{ formatarMoeda(lead.valor_monetario || lead.valor_total || 0) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </b-modal>

    <!-- Modal de Sucesso - Criar Campanha -->
    <b-modal
      v-model="modalSucessoAberto"
      title="Audiência Criada com Sucesso!"
      centered
      hide-footer
    >
      <div class="text-center py-4">
        <div class="success-icon mb-3">
          <i
            class="feather icon-check-circle text-success"
            style="font-size: 4rem;"
          />
        </div>
        <h5>Audiência "{{ nomeAudienciaCreated }}" criada!</h5>
        <p class="text-muted">
          {{ quantidadeLeadsUltimaAudiencia }} leads foram adicionados à audiência.
        </p>
        <p class="text-muted small">
          Agora você pode usar essa audiência para criar campanhas direcionadas.
        </p>

        <div class="d-flex justify-content-center gap-2 mt-4">
          <b-button
            variant="outline-secondary"
            @click="modalSucessoAberto = false"
          >
            Fechar
          </b-button>
          <b-button
            variant="primary"
            @click="irParaCampanhas"
          >
            <i class="feather icon-send mr-1" />
            Criar Campanha
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BSpinner,
  BFormSelect,
  BFormSelectOption,
  BFormInput,
  BFormGroup,
  BFormCheckbox,
  BBadge,
  BModal,
  VBTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import stores from '../../functions/basics/stores'
import { getRFM } from '../../functions/basics/analytics'
import { createAudience, getAudiences } from '../../functions/basics/campaigns'
import {
  RFM_SEGMENTS,
  processarMatrizRFM,
  getClientesPorSegmento,
} from '../dashboard/main/services/rfmSegmentation'

export default {
  name: 'RFMAudiencia',
  components: {
    BCard,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BSpinner,
    BFormSelect,
    BFormSelectOption,
    BFormInput,
    BFormGroup,
    BFormCheckbox,
    BBadge,
    BModal,
    vSelect,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      // Estado geral
      carregando: false,
      carregandoLeads: false,
      carregandoLeadsModal: false,
      salvandoAudiencia: false,

      // Lojas
      stores: [],
      storeUuid: '',

      // Período
      dataInicio: '',
      dataFim: '',

      // Dados RFM
      dadosRFM: {
        matrizRFM: {},
        clientes: [],
        segmentos: {},
      },
      dadosSegmentos: {},
      totalLeads: 0,

      // Seleção
      segmentosSelecionados: [],
      nomeAudiencia: '',
      /** Quantidade máxima de leads na audiência (1–50.000), alinhado à audiência por filtros */
      limiteLeadsRFM: 1000,
      quantidadeLeadsUltimaAudiencia: 0,

      // Filtro RFM 3D (Qualificação Monetária)
      qualificarPorMonetario: false,
      dadosSegmentos3D: {}, // Totais filtrados por M > 3

      // Modal de leads
      modalLeadsAberto: false,
      leadsVisualizacao: [],
      totalLeadsAPI: 0,

      // Modal de sucesso
      modalSucessoAberto: false,
      nomeAudienciaCreated: '',
      audienciaCreatedId: null,

      // Exclusão de audiências existentes
      audienciasDisponiveis: [],
      audienciasExcluir: [],
      carregandoAudiencias: false,
    }
  },

  computed: {
    storeOptions() {
      return this.stores.map(store => ({
        value: store.uuid,
        text: store.name || `Loja ${store.uuid.substring(0, 8)}`,
      }))
    },

    segmentosDisponiveis() {
      return Object.values(RFM_SEGMENTS)
    },

    audienciasParaExclusao() {
      return this.audienciasDisponiveis.map(aud => ({
        id: aud.id,
        name: aud.name || 'Audiência sem nome',
        lead_count: aud.lead_count || 0,
        created_at: aud.created_at,
      }))
    },

    // Total de leads selecionados (com ou sem filtro 3D)
    totalLeadsSelecionados() {
      // Se filtro 3D ativo, usar dados filtrados
      const dados = this.qualificarPorMonetario ? this.dadosSegmentos3D : this.dadosSegmentos
      return this.segmentosSelecionados.reduce((total, segId) => {
        const segData = dados[segId]
        return total + ((segData && segData.total) ? segData.total : 0)
      }, 0)
    },

    // Total sem filtro (para comparação)
    totalLeadsSemFiltro() {
      return this.segmentosSelecionados.reduce((total, segId) => {
        const segData = this.dadosSegmentos[segId]
        return total + ((segData && segData.total) ? segData.total : 0)
      }, 0)
    },
  },

  watch: {
    limiteLeadsRFM(newVal) {
      const n = Number(newVal)
      if (Number.isFinite(n) && n > 50000) {
        this.limiteLeadsRFM = 50000
        this.$bvToast.toast('O limite máximo é de 50.000 contatos. Valor ajustado automaticamente.', {
          title: 'Atenção',
          variant: 'warning',
          solid: true,
        })
      }
    },
  },

  async mounted() {
    // Definir período padrão (últimos 182 dias)
    const hoje = new Date()
    const seisMesesAtras = new Date()
    seisMesesAtras.setDate(hoje.getDate() - 182)

    this.dataFim = hoje.toISOString().split('T')[0]
    this.dataInicio = seisMesesAtras.toISOString().split('T')[0]

    // Carregar lojas
    try {
      this.stores = await stores()

      if (this.stores.length >= 1) {
        this.storeUuid = this.stores[0].uuid
        await Promise.all([
          this.carregarDadosRFM(),
          this.carregarAudienciasDisponiveis(),
        ])
      }
    } catch (error) {
      console.error('Erro ao carregar lojas:', error)
      this.$bvToast.toast('Erro ao carregar lojas', {
        title: 'Erro',
        variant: 'danger',
        solid: true,
      })
    }
  },

  methods: {
    async onStoreChange() {
      if (this.storeUuid) {
        await Promise.all([
          this.carregarDadosRFM(),
          this.carregarAudienciasDisponiveis(),
        ])
      }
    },

    async carregarAudienciasDisponiveis() {
      if (!this.storeUuid) return

      this.carregandoAudiencias = true
      try {
        const audiencias = await getAudiences(this.storeUuid)
        this.audienciasDisponiveis = audiencias || []
        console.log(`✅ ${this.audienciasDisponiveis.length} audiências carregadas para exclusão`)
      } catch (error) {
        console.error('❌ Erro ao carregar audiências:', error)
        this.audienciasDisponiveis = []
      } finally {
        this.carregandoAudiencias = false
      }
    },

    formatarDataAudiencia(data) {
      if (!data) return ''
      try {
        const date = new Date(data)
        return date.toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      } catch {
        return ''
      }
    },

    async carregarDadosRFM() {
      if (!this.storeUuid) return

      this.carregando = true

      try {
        const response = await getRFM(this.storeUuid, this.dataInicio, this.dataFim)

        if (response && response.data) {
          console.log('📊 [RFM Audiencia] Dados recebidos da API:', response.data)

          // Armazenar clientes_por_segmento da API
          this.dadosRFM.clientesPorSegmento = response.data.clientes_por_segmento || {}

          // Criar array de todos os clientes a partir de clientes_por_segmento
          // (mesma lógica do Dashboard)
          const todosClientes = []
          if (response.data.clientes_por_segmento) {
            Object.entries(response.data.clientes_por_segmento).forEach(([, dados]) => {
              if (dados.clientes && Array.isArray(dados.clientes)) {
                todosClientes.push(...dados.clientes)
              }
            })
          }

          this.dadosRFM.clientes = todosClientes

          // IMPORTANTE: Usar a lógica de segmentação 2D (R x F) igual ao Dashboard
          // Isso garante que cada lead pertença a apenas um segmento
          const matrizProcessada = processarMatrizRFM({
            matriz_rfm: response.data.matriz_rfm,
            clientes: todosClientes,
            clientes_por_segmento: response.data.clientes_por_segmento,
          })

          this.dadosRFM.matrizRFM = matrizProcessada

          // Calcular totais baseado na matriz 2D processada (igual ao Dashboard)
          // Isso conta os clientes REAIS que estão na matriz - os mesmos que aparecem no modal
          this.dadosSegmentos = {}
          Object.values(RFM_SEGMENTS).forEach(seg => {
            // Contar clientes reais na matriz para este segmento
            const clientesDoSegmento = getClientesPorSegmento(matrizProcessada, seg.nome)

            this.dadosSegmentos[seg.id] = {
              total: clientesDoSegmento.length, // Total REAL de clientes disponíveis
              nome: seg.nome,
            }
          })

          // Calcular total geral
          this.totalLeads = Object.values(this.dadosSegmentos).reduce(
            (total, seg) => total + (seg.total || 0),
            0,
          )

          console.log('📊 [RFM Audiencia] Dados carregados (2D):', {
            totalLeads: this.totalLeads,
            segmentos: this.dadosSegmentos,
          })
        }
      } catch (error) {
        console.error('Erro ao carregar dados RFM:', error)
        this.$bvToast.toast('Erro ao carregar dados RFM', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.carregando = false
      }
    },

    toggleSegmento(segmentoId) {
      const index = this.segmentosSelecionados.indexOf(segmentoId)
      if (index === -1) {
        this.segmentosSelecionados.push(segmentoId)
      } else {
        this.segmentosSelecionados.splice(index, 1)
      }
    },

    limparSelecao() {
      this.segmentosSelecionados = []
      this.nomeAudiencia = ''
      this.limiteLeadsRFM = 1000
      this.qualificarPorMonetario = false
      this.audienciasExcluir = []
    },

    // Handler para mudança no filtro de qualificação monetária
    onQualificacaoChange() {
      console.log('🎯 [RFM 3D] Filtro de qualificação alterado:', this.qualificarPorMonetario)

      if (this.qualificarPorMonetario) {
        // Calcular totais filtrados por M > 3
        this.calcularTotais3D()
      }
    },

    // Calcula os totais de cada segmento considerando apenas M > 3
    calcularTotais3D() {
      this.dadosSegmentos3D = {}

      Object.values(RFM_SEGMENTS).forEach(seg => {
        // Buscar clientes do segmento na matriz 2D
        const clientesDoSegmento = getClientesPorSegmento(this.dadosRFM.matrizRFM, seg.nome)

        // Filtrar apenas clientes com Score Monetário > 3
        const clientesAltoValor = clientesDoSegmento.filter(cliente => {
          const scoreM = this.getScoreMonetario(cliente)
          return scoreM > 3
        })

        this.dadosSegmentos3D[seg.id] = {
          total: clientesAltoValor.length,
          nome: seg.nome,
          clientes: clientesAltoValor,
        }

        console.log(`📊 [RFM 3D] Segmento "${seg.nome}": ${clientesDoSegmento.length} total → ${clientesAltoValor.length} alto valor (M > 3)`)
      })
    },

    // Obtém o score monetário de um cliente
    getScoreMonetario(cliente) {
      // Prioridade: rfm_scores.m > m > valor_monetario calculado
      if (cliente.rfm_scores && cliente.rfm_scores.m !== undefined) {
        return cliente.rfm_scores.m
      }
      if (cliente.m !== undefined) {
        return cliente.m
      }
      // Se não tiver score M, retorna 0 (será filtrado)
      return 0
    },

    // Calcula a redução percentual ao aplicar filtro 3D
    calcularReducaoPercentual() {
      if (this.totalLeadsSemFiltro === 0) return 0
      const reducao = ((this.totalLeadsSemFiltro - this.totalLeadsSelecionados) / this.totalLeadsSemFiltro) * 100
      return reducao.toFixed(1)
    },

    // Obtém os leads filtrados (com ou sem qualificação monetária)
    getLeadsFiltrados(segmentoId) {
      const segmento = this.segmentosDisponiveis.find(s => s.id === segmentoId)
      const nomeSegmento = (segmento && segmento.nome) ? segmento.nome : segmentoId

      // Buscar todos os clientes do segmento na matriz 2D
      const clientesDoSegmento = getClientesPorSegmento(this.dadosRFM.matrizRFM, nomeSegmento)

      // Se filtro 3D ativo, filtrar por M > 3
      if (this.qualificarPorMonetario) {
        return clientesDoSegmento.filter(cliente => {
          const scoreM = this.getScoreMonetario(cliente)
          return scoreM > 3
        })
      }

      return clientesDoSegmento
    },

    getSegmentoTotal(segmentoId) {
      if (this.dadosSegmentos && this.dadosSegmentos[segmentoId]) {
        return this.dadosSegmentos[segmentoId].total || 0
      }
      return 0
    },

    calcularPercentual(segmentoId) {
      if (this.totalLeads === 0) return '0.0'
      const total = this.getSegmentoTotal(segmentoId)
      return ((total / this.totalLeads) * 100).toFixed(1)
    },

    getLeadScoreR(lead) {
      if (lead.rfm_scores && lead.rfm_scores.r) {
        return lead.rfm_scores.r
      }
      return lead.r || '-'
    },

    getLeadScoreF(lead) {
      if (lead.rfm_scores && lead.rfm_scores.f) {
        return lead.rfm_scores.f
      }
      return lead.f || '-'
    },

    getLeadScoreRNum(lead) {
      const v = this.getLeadScoreR(lead)
      const n = Number(v)
      return Number.isFinite(n) ? n : 0
    },

    getLeadScoreFNum(lead) {
      const v = this.getLeadScoreF(lead)
      const n = Number(v)
      return Number.isFinite(n) ? n : 0
    },

    /**
     * Prioriza melhores scores RFM antes de cortar pelo limite (evita ordem arbitrária do concat por segmento).
     */
    ordenarLeadsParaLimite(leads) {
      return [...leads].sort((a, b) => {
        const rDiff = this.getLeadScoreRNum(b) - this.getLeadScoreRNum(a)
        if (rDiff !== 0) return rDiff
        const fDiff = this.getLeadScoreFNum(b) - this.getLeadScoreFNum(a)
        if (fDiff !== 0) return fDiff
        return this.getScoreMonetario(b) - this.getScoreMonetario(a)
      })
    },

    aplicarLimiteLeads(leadsUnicos) {
      let limite = parseInt(this.limiteLeadsRFM, 10)
      if (!Number.isFinite(limite) || limite < 1) limite = 1
      limite = Math.min(limite, 50000)
      if (leadsUnicos.length <= limite) return leadsUnicos
      return this.ordenarLeadsParaLimite(leadsUnicos).slice(0, limite)
    },

    getSegmentIcon(segmentoId) {
      const icons = {
        campeoes: 'feather icon-award',
        clientes_leais: 'feather icon-star',
        potenciais_campeoes: 'feather icon-trending-up',
        recem_chegados: 'feather icon-user-plus',
        em_risco: 'feather icon-alert-triangle',
        hibernando: 'feather icon-moon',
        outros: 'feather icon-users',
      }
      return icons[segmentoId] || 'feather icon-users'
    },

    getSegmentoNome(segmentoId) {
      const segmento = this.segmentosDisponiveis.find(s => s.id === segmentoId)
      return (segmento && segmento.nomeDisplay) ? segmento.nomeDisplay : segmentoId
    },

    getSegmentoBadgeVariant(segmentoId) {
      const segmento = this.segmentosDisponiveis.find(s => s.id === segmentoId)
      return (segmento && segmento.corBadge) ? segmento.corBadge : 'secondary'
    },

    formatNumber(num) {
      return new Intl.NumberFormat('pt-BR').format(num)
    },

    formatarMoeda(valor) {
      if (!valor) return '0,00'
      return Number(valor).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    },

    async visualizarLeads() {
      if (this.segmentosSelecionados.length === 0) {
        this.$bvToast.toast('Selecione pelo menos um segmento', {
          title: 'Atenção',
          variant: 'warning',
          solid: true,
        })
        return
      }

      this.carregandoLeads = true
      this.carregandoLeadsModal = true
      this.modalLeadsAberto = true

      try {
        // Coletar leads de todos os segmentos selecionados
        // IMPORTANTE: Usar a matriz 2D processada como fonte de verdade (igual aos totais)
        // Se filtro 3D ativo, aplicar filtro de M > 3
        const todosLeads = []

        for (const segmentoId of this.segmentosSelecionados) {
          // Usar método que já aplica o filtro 3D se necessário
          const leadsSegmento = this.getLeadsFiltrados(segmentoId)

          const segmento = this.segmentosDisponiveis.find(s => s.id === segmentoId)
          const nomeSegmento = (segmento && segmento.nome) ? segmento.nome : segmentoId

          console.log(`👥 [Visualizar] Segmento "${nomeSegmento}": ${leadsSegmento.length} clientes ${this.qualificarPorMonetario ? '(filtro 3D ativo)' : '(2D)'}`)

          leadsSegmento.forEach(lead => {
            const leadComSegmento = { ...lead, segmento_id: segmentoId }
            todosLeads.push(leadComSegmento)
          })
        }

        // Remover duplicatas (caso existam por algum motivo)
        const leadsUnicos = []
        const idsVistos = new Set()

        for (const lead of todosLeads) {
          const id = lead.cliente_store_id || lead.id || `${lead.phone}_${lead.nome}`
          if (!idsVistos.has(id)) {
            idsVistos.add(id)
            leadsUnicos.push(lead)
          }
        }

        this.leadsVisualizacao = leadsUnicos.slice(0, 100) // Limitar para performance
        this.totalLeadsAPI = leadsUnicos.length

        console.log('👥 Leads carregados:', {
          total: leadsUnicos.length,
          mostrados: this.leadsVisualizacao.length,
          filtro3D: this.qualificarPorMonetario,
        })

        // Avisar se não encontrou leads
        if (leadsUnicos.length === 0) {
          const mensagem = this.qualificarPorMonetario
            ? 'Nenhum lead de alto valor (M > 3) encontrado para os segmentos selecionados.'
            : 'Nenhum lead encontrado para os segmentos selecionados. Os dados podem estar sendo processados.'

          this.$bvToast.toast(mensagem, {
            title: 'Atenção',
            variant: 'warning',
            solid: true,
            autoHideDelay: 5000,
          })
        }
      } catch (error) {
        console.error('Erro ao carregar leads:', error)
        this.$bvToast.toast('Erro ao carregar leads', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.carregandoLeads = false
        this.carregandoLeadsModal = false
      }
    },

    async criarAudienciaRFM() {
      if (!this.nomeAudiencia.trim()) {
        this.$bvToast.toast('Digite um nome para a audiência', {
          title: 'Atenção',
          variant: 'warning',
          solid: true,
        })
        return
      }

      if (this.segmentosSelecionados.length === 0) {
        this.$bvToast.toast('Selecione pelo menos um segmento', {
          title: 'Atenção',
          variant: 'warning',
          solid: true,
        })
        return
      }

      const limiteInformado = parseInt(this.limiteLeadsRFM, 10)
      if (!Number.isFinite(limiteInformado) || limiteInformado < 1) {
        this.$bvToast.toast('Informe uma quantidade de leads entre 1 e 50.000', {
          title: 'Atenção',
          variant: 'warning',
          solid: true,
        })
        return
      }

      this.salvandoAudiencia = true

      try {
        // Coletar IDs dos leads de todos os segmentos selecionados
        // IMPORTANTE: Usar a matriz 2D processada como fonte de verdade (igual aos totais e visualização)
        // Se filtro 3D ativo, aplicar filtro de M > 3
        let todosLeads = []

        console.log('🔍 Segmentos selecionados:', this.segmentosSelecionados)
        console.log('🎯 Filtro 3D (M > 3):', this.qualificarPorMonetario)

        for (const segmentoId of this.segmentosSelecionados) {
          // Usar método que já aplica o filtro 3D se necessário
          const leadsSegmento = this.getLeadsFiltrados(segmentoId)

          const segmento = this.segmentosDisponiveis.find(s => s.id === segmentoId)
          const nomeSegmento = (segmento && segmento.nome) ? segmento.nome : segmentoId

          console.log(`📊 Segmento "${nomeSegmento}": ${leadsSegmento.length} leads ${this.qualificarPorMonetario ? '(filtro 3D ativo)' : '(2D)'}`)
          todosLeads = todosLeads.concat(leadsSegmento)
        }

        console.log(`📊 Total de leads coletados (com duplicatas): ${todosLeads.length}`)

        // Remover duplicatas
        const leadsUnicos = []
        const idsVistos = new Set()

        for (const lead of todosLeads) {
          const id = lead.cliente_store_id || lead.id
          if (id && !idsVistos.has(id)) {
            idsVistos.add(id)
            leadsUnicos.push(lead)
          }
        }

        console.log(`📊 Total de leads únicos: ${leadsUnicos.length}`)

        // Validar que há leads para criar a audiência
        if (leadsUnicos.length === 0) {
          this.$bvToast.toast('Nenhum lead encontrado nos segmentos selecionados. Verifique se os dados RFM foram carregados corretamente.', {
            title: 'Atenção',
            variant: 'warning',
            solid: true,
            autoHideDelay: 5000,
          })
          return
        }

        const leadsFinal = this.aplicarLimiteLeads(leadsUnicos)
        const quantidadeMaterializar = leadsFinal.length

        const segmentosNomes = this.segmentosSelecionados.map(s => this.getSegmentoNome(s)).join(', ')
        const filtro3DTexto = this.qualificarPorMonetario ? ' [Alto Valor - M > 3]' : ''
        let descricao = `Audiência RFM${filtro3DTexto}: ${segmentosNomes}. Período: ${this.dataInicio} a ${this.dataFim}. Total: ${leadsFinal.length} leads (máx. solicitado: ${Math.min(parseInt(this.limiteLeadsRFM, 10) || 0, 50000)}).`
        if (descricao.length > 990) {
          descricao = `${descricao.substring(0, 990)}...`
        }

        const sourceKql = this.gerarKQLRFM(leadsFinal, quantidadeMaterializar)

        console.log('📊 Leads únicos para audiência RFM:', leadsFinal.length, `(pool: ${leadsUnicos.length})`)
        console.log('📋 KQL gerado:', `${sourceKql.substring(0, 200)}...`)

        const audienceData = {
          name: this.nomeAudiencia,
          description: descricao,
          source_kql: sourceKql,
          filters: {
            limite: quantidadeMaterializar,
          },
        }

        if (this.audienciasExcluir && this.audienciasExcluir.length > 0) {
          audienceData.filters.excluir_audiencias = this.audienciasExcluir
          console.log('🚫 Audiências para exclusão:', this.audienciasExcluir)
        }

        console.log('📤 Enviando audiência RFM:', {
          name: audienceData.name,
          description: audienceData.description,
          kqlLength: audienceData.source_kql.length,
          kqlPreview: audienceData.source_kql.substring(0, 300),
        })

        const response = await createAudience(this.storeUuid, audienceData)

        console.log('✅ Audiência RFM criada:', response)

        // Verificar se a audiência está em processamento assíncrono
        if (response && response.status === 'processing') {
          console.log('⏳ Audiência em processamento assíncrono')
        }

        this.nomeAudienciaCreated = this.nomeAudiencia
        this.audienciaCreatedId = (response && response.id) ? response.id : null
        this.quantidadeLeadsUltimaAudiencia = leadsFinal.length

        // Limpar seleção
        this.limparSelecao()

        // Mostrar modal de sucesso
        this.modalSucessoAberto = true

        this.$bvToast.toast('Audiência criada com sucesso!', {
          title: 'Sucesso',
          variant: 'success',
          solid: true,
        })
      } catch (error) {
        console.error('❌ Erro ao criar audiência RFM:', error)

        // Log detalhado do erro
        if (error.response) {
          console.error('📋 Status:', error.response.status)
          console.error('📋 Data:', JSON.stringify(error.response.data, null, 2))
        }

        const errorData = (error && error.response && error.response.data) ? error.response.data : null
        let errorMessage = 'Erro ao criar audiência'

        if (errorData) {
          // Tentar extrair mensagem de erro específica
          errorMessage = errorData.error_message
            || errorData.error
            || errorData.message
            || (errorData.details ? `Erro: ${errorData.details}` : null)
            || errorMessage
        } else if (error.message) {
          errorMessage = error.message
        }

        this.$bvToast.toast(errorMessage, {
          title: 'Erro ao criar audiência',
          variant: 'danger',
          solid: true,
          autoHideDelay: 8000,
        })
      } finally {
        this.salvandoAudiencia = false
      }
    },

    gerarKQLRFM(leadsUnicos, limiteTake) {
      // Gerar query KQL válida usando os IDs dos clientes
      // O campo rfm_segment não existe no ADX - precisamos filtrar por cliente_store_id
      let takeN = parseInt(limiteTake, 10)
      if (!Number.isFinite(takeN) || takeN < 1) {
        takeN = Math.min(Math.max(parseInt(this.limiteLeadsRFM, 10) || 1, 1), 50000)
      } else {
        takeN = Math.min(takeN, 50000)
      }

      if (!leadsUnicos || leadsUnicos.length === 0) {
        // Query básica sem leads específicos
        return `clientes | where store_uuid == "${this.storeUuid}" and isnotempty(phone) | take 0`
      }

      // Coletar IDs válidos
      const idsValidos = leadsUnicos
        .map(l => l.cliente_store_id || l.id)
        .filter(id => id != null && String(id).trim() !== '')

      if (idsValidos.length === 0) {
        return `clientes | where store_uuid == "${this.storeUuid}" and isnotempty(phone) | take 0`
      }

      // IMPORTANTE: O ADX tem limite de tamanho para queries
      // Para muitos IDs, usar uma abordagem diferente
      const maxIdsPerQuery = 50000 // Limite seguro para evitar erros no ADX

      let idsParaUsar = idsValidos
      if (idsValidos.length > maxIdsPerQuery) {
        // Para muitos IDs, usar apenas os primeiros (limitação técnica)
        console.warn(`⚠️ Muitos IDs (${idsValidos.length}), limitando para ${maxIdsPerQuery}`)
        idsParaUsar = idsValidos.slice(0, maxIdsPerQuery)
      }

      // Escapar IDs para evitar problemas com caracteres especiais
      const idsFormatados = idsParaUsar
        .map(id => `"${String(id).replace(/"/g, '\\"')}"`)
        .join(', ')

      const takeFinal = Math.min(
        takeN,
        idsParaUsar.length,
      )

      // Query em uma linha para evitar problemas de parsing
      // Formato: clientes | where ... | summarize ... | project ...
      const query = [
        'clientes',
        `| where store_uuid == "${this.storeUuid}" and isnotempty(phone) and cliente_store_id != "cliente_store_id" and name != "data_pedido" and cliente_store_id in (${idsFormatados})`,
        '| summarize arg_max(name, phone, email) by cliente_store_id',
        '| project cliente_store_id, name, phone, email',
      ].join(' ')

      return `${query} | take ${takeFinal}`
    },

    exportarCSV() {
      if (this.leadsVisualizacao.length === 0) {
        this.$bvToast.toast('Nenhum lead para exportar', {
          title: 'Atenção',
          variant: 'warning',
          solid: true,
        })
        return
      }

      try {
        const headers = ['Nome', 'Telefone', 'Email', 'Segmento', 'Score R', 'Score F', 'Score M', 'Valor Total']

        const rows = this.leadsVisualizacao.map(lead => [
          lead.nome || lead.name || '',
          lead.phone || lead.telefone || '',
          lead.email || '',
          this.getSegmentoNome(lead.segmento_id || lead.segmento),
          this.getLeadScoreR(lead),
          this.getLeadScoreF(lead),
          this.getScoreMonetario(lead) || '',
          lead.valor_monetario || lead.valor_total || 0,
        ])

        const csvContent = [
          headers.join(','),
          ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
        ].join('\n')

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)

        const nomeArquivo = `rfm_leads_${this.segmentosSelecionados.join('_')}_${new Date().toISOString().split('T')[0]}.csv`

        link.setAttribute('href', url)
        link.setAttribute('download', nomeArquivo)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        this.$bvToast.toast(`CSV exportado com ${this.leadsVisualizacao.length} leads`, {
          title: 'Sucesso',
          variant: 'success',
          solid: true,
        })
      } catch (error) {
        console.error('Erro ao exportar CSV:', error)
        this.$bvToast.toast('Erro ao exportar CSV', {
          title: 'Erro',
          variant: 'danger',
          solid: true,
        })
      }
    },

    irParaCampanhas() {
      this.modalSucessoAberto = false
      this.$router.push({
        name: 'campaign-oficiais',
        params: { uuid: this.storeUuid },
      })
    },
  },
}
</script>

<style scoped>
.rfm-audiencia-page {
  padding: 1rem;
}

@media (min-width: 768px) {
  .rfm-audiencia-page {
    padding: 1.5rem;
  }
}

/* ===== HEADER RESPONSIVO ===== */
.page-header {
  margin-bottom: 1.5rem;
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

@media (min-width: 768px) {
  .header-content {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
  }
}

.header-title h4 {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

@media (min-width: 768px) {
  .header-title h4 {
    font-size: 1.5rem;
  }
}

/* ===== PERÍODO RESPONSIVO ===== */
.period-card .card-body {
  padding: 1rem;
}

@media (min-width: 768px) {
  .period-card .card-body {
    padding: 1.25rem;
  }
}

.period-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

@media (min-width: 768px) {
  .period-content {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
}

.period-info h6 {
  font-size: 0.95rem;
  font-weight: 600;
}

.period-inputs {
  width: 100%;
}

@media (min-width: 768px) {
  .period-inputs {
    width: auto;
    min-width: 400px;
  }
}

.date-input {
  width: 100%;
}

@media (min-width: 768px) {
  .date-input {
    width: 150px;
  }
}

.apply-btn {
  width: 100%;
}

@media (min-width: 768px) {
  .apply-btn {
    width: auto;
  }
}

/* ===== SEGMENT CARDS ===== */
.segment-card {
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
  height: 100%;
}

.segment-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.segment-card.selected {
  border-color: var(--primary);
  background-color: rgba(var(--primary-rgb), 0.05);
}

.segment-card .card-body {
  padding: 1rem;
  display: flex;
  flex-direction: column;
}

@media (min-width: 768px) {
  .segment-card .card-body {
    padding: 1.25rem;
  }
}

.segment-description {
  min-height: 40px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-clamp: 2;
  overflow: hidden;
  line-height: 1.4;
}

.segment-action {
  margin-top: auto;
  padding-top: 0.5rem;
}

.segment-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  flex-shrink: 0;
}

@media (min-width: 768px) {
  .segment-icon {
    width: 48px;
    height: 48px;
    font-size: 1.5rem;
  }
}

.segment-stats {
  background: #f8f9fa;
  padding: 0.75rem;
  border-radius: 8px;
  margin-top: 0.75rem;
}

/* ===== RESUMO DA SELEÇÃO RESPONSIVO ===== */
.selection-summary {
  border: 2px solid var(--primary);
  background: linear-gradient(135deg, rgba(var(--primary-rgb), 0.02) 0%, rgba(var(--primary-rgb), 0.05) 100%);
}

.selection-summary .card-body {
  padding: 1rem;
}

@media (min-width: 768px) {
  .selection-summary .card-body {
    padding: 1.5rem;
  }
}

.summary-header {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

@media (min-width: 768px) {
  .summary-header {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
  }
}

.summary-title {
  flex: 1;
}

.summary-stats {
  flex-shrink: 0;
}

.badge-removable {
  font-size: 0.85rem;
  padding: 0.4em 0.8em;
  display: inline-flex;
  align-items: center;
}

.badge-removable .feather {
  margin-left: 0.25rem;
  font-size: 0.75rem;
}

/* ===== AÇÕES RESPONSIVAS ===== */
.actions-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.actions-section-fields .actions-field-nome {
  min-width: 0;
}

@media (min-width: 768px) {
  .actions-section-fields .actions-input-limite {
    width: 11rem;
    min-width: 11rem;
  }
}

.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

@media (min-width: 768px) {
  .action-buttons {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
}

/* ===== BADGES ===== */
.badge {
  font-weight: 500;
  padding: 0.4em 0.8em;
}

/* ===== SEGMENT COLORS ===== */
.segment-champions .segment-icon { background-color: rgba(40, 167, 69, 0.1) !important; color: #28a745 !important; }
.segment-loyal .segment-icon { background-color: rgba(0, 123, 255, 0.1) !important; color: #007bff !important; }
.segment-potential .segment-icon { background-color: rgba(23, 162, 184, 0.1) !important; color: #17a2b8 !important; }
.segment-new-customers .segment-icon { background-color: rgba(111, 66, 193, 0.1) !important; color: #6f42c1 !important; }
.segment-at-risk .segment-icon { background-color: rgba(253, 126, 20, 0.1) !important; color: #fd7e14 !important; }
.segment-about-to-sleep .segment-icon { background-color: rgba(220, 53, 69, 0.1) !important; color: #dc3545 !important; }
.segment-promising .segment-icon { background-color: rgba(108, 117, 125, 0.1) !important; color: #6c757d !important; }

/* ===== TABLE ===== */
.table th {
  font-weight: 600;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.table td {
  vertical-align: middle;
  font-size: 0.9rem;
}

@media (max-width: 767px) {
  .table th,
  .table td {
    font-size: 0.8rem;
    padding: 0.5rem;
  }
}

/* ===== UTILITIES ===== */
.cursor-pointer {
  cursor: pointer;
}

.gap-2 {
  gap: 0.5rem;
}

.w-md-auto {
  width: 100%;
}

@media (min-width: 768px) {
  .w-md-auto {
    width: auto !important;
  }
}

/* ===== SUCCESS ICON ANIMATION ===== */
.success-icon {
  animation: successPulse 0.5s ease-out;
}

@keyframes successPulse {
  0% { transform: scale(0.8); opacity: 0; }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); opacity: 1; }
}

/* ===== FILTRO RFM 3D ===== */
.rfm-3d-filter {
  background: linear-gradient(135deg, rgba(40, 167, 69, 0.05) 0%, rgba(40, 167, 69, 0.1) 100%);
  border: 1px solid rgba(40, 167, 69, 0.2);
  border-radius: 12px;
  padding: 1rem;
}

.rfm-3d-filter .custom-switch {
  padding-left: 2.5rem;
}

.rfm-3d-filter .custom-control-label {
  cursor: pointer;
}

.filter-info {
  flex: 1;
}

.filter-badge {
  flex-shrink: 0;
}

.filter-comparison {
  background: rgba(40, 167, 69, 0.08);
  border-radius: 8px;
  padding: 0.5rem 1rem;
}

@media (max-width: 767px) {
  .rfm-3d-filter {
    padding: 0.75rem;
  }

  .filter-comparison {
    overflow-x: auto;
  }

  .filter-comparison .d-flex {
    flex-wrap: nowrap;
    white-space: nowrap;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .rfm-audiencia-page {
    padding: 1rem;
  }

  .d-flex.justify-content-between {
    flex-direction: column;
    gap: 1rem;
  }
}
</style>
